import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Skeleton, Stack } from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import { FlowNotableObject } from 'flow-schema';
import { isObject } from 'lodash';
import pluralize from 'pluralize';

import { Alert, ExternalLink, Text, api, isDefined } from 'common';

import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';
import type { OutputContentProps } from '../../types';
import { DefaultReturnValueContent } from '../Default/OutputContent';

interface PatientUploadWithFileReference {
  gsPath: string;
  documentType: string;
  fileReference: string;
  patientInsuranceInformationId?: string;
}

export function PatientUploadOutputContent(
  props: OutputContentProps<api.FlowDefinition.ProducedType.Any>
) {
  const { metadata, isFullHeightView } = props;
  const rawReturnValue: unknown = props.returnValue;

  const returnValue =
    isDefined(rawReturnValue) &&
    Array.isArray(rawReturnValue) &&
    rawReturnValue.every(
      (item) =>
        isObject(item) && 'gsPath' in item && 'documentType' in item && 'fileReference' in item
    )
      ? (rawReturnValue as PatientUploadWithFileReference[])
      : undefined;

  const { value: notableObject } =
    useSelectedNodeField<api.FlowDefinition.GetNotableObjectConfig['notableObject']>(
      'config.notableObject'
    );

  const documentResults = useQueries({
    queries:
      Array.isArray(returnValue) && notableObject === FlowNotableObject.PatientUpload
        ? returnValue.map((upload: PatientUploadWithFileReference) => ({
            queryKey: [
              api.client.File.baseEndpoint,
              'api.client.File.generateReadUrl',
              'FlowBuilderNodeConfigGetNotableObjectOutputContent',
              upload,
            ],
            queryFn: () => api.client.File.generateReadUrl(upload.fileReference),
            staleTime: 5 * 60 * 1000, // 5 minutes
          }))
        : [],
  });

  if (notableObject !== FlowNotableObject.PatientUpload) {
    return (
      <DefaultReturnValueContent
        returnValue={rawReturnValue}
        metadata={metadata}
        isFullHeightView={isFullHeightView}
      />
    );
  }

  if (!isDefined(returnValue) || !Array.isArray(returnValue) || returnValue.length === 0) {
    return (
      <Stack spacing={2}>
        <Text variant="heading5" bold>
          Patient Uploads
        </Text>
        <Text>No patient uploads found</Text>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Text bold>{pluralize('Upload', returnValue.length, true)}</Text>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            {documentResults.map((documentResult, index) => {
              const upload = returnValue[index];

              if (documentResult.isPending) {
                return (
                  <Stack key={upload.fileReference} direction="row" spacing={2} alignItems="center">
                    <Skeleton variant="text" width="200px" />
                    <Skeleton variant="text" width="100px" />
                  </Stack>
                );
              }

              if (documentResult.isError) {
                return (
                  <Stack key={upload.fileReference} direction="row" spacing={2} alignItems="center">
                    <Alert error={documentResult.error}>
                      Failed to load document: {upload.gsPath}
                    </Alert>
                  </Stack>
                );
              }

              if (!isDefined(documentResult.data)) {
                return (
                  <Stack key={upload.fileReference} direction="row" spacing={2} alignItems="center">
                    <Text>No data available for: {upload.gsPath}</Text>
                  </Stack>
                );
              }

              if (!isDefined(documentResult.data.signedReadUrl)) {
                return (
                  <Stack key={upload.fileReference} direction="row" spacing={2} alignItems="center">
                    <Text>No signed URL available for: {upload.gsPath}</Text>
                  </Stack>
                );
              }

              return (
                <Stack key={upload.fileReference} direction="row" spacing={2} alignItems="center">
                  <Text>
                    <ExternalLink
                      href={documentResult.data.signedReadUrl}
                      onClick={(event: React.MouseEvent) => event.stopPropagation()}
                    >
                      {upload.gsPath}
                    </ExternalLink>
                  </Text>
                </Stack>
              );
            })}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Text bold>Raw:</Text>
      <DefaultReturnValueContent
        returnValue={returnValue}
        metadata={metadata}
        isFullHeightView={isFullHeightView}
      />
    </Stack>
  );
}
